import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, Check } from "lucide-react";

const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Search...",
  valueKey = "value",
  labelKey = "label",
  subLabelKey = "",
  searchKeys = [],
  className = "",
  noDataText = "No data found",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedItem = useMemo(
    () => options.find((item) => item[valueKey] === value),
    [options, value, valueKey],
  );

  const filteredOptions = useMemo(() => {
    const q = search.toLowerCase().trim();

    if (!q) return options;

    return options.filter((item) =>
      searchKeys.some((key) =>
        String(item[key] || "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [options, search, searchKeys]);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest?.("[data-searchable-dropdown]")) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", close);

    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className={`relative ${className}`} data-searchable-dropdown>
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />

        <input
          value={open ? search : selectedItem?.[labelKey] || ""}
          onFocus={() => {
            setOpen(true);
            setSearch("");
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          placeholder={placeholder}
          className="h-11 w-full rounded-xl border border-gray-300 bg-white pl-9 pr-10 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />

        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className="absolute right-2 top-1/2 -translate-y-1/2"
        >
          <ChevronDown
            size={18}
            className={`transition ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <div className="absolute z-50 mt-2 max-h-44 w-full overflow-auto rounded-xl border bg-white shadow-xl">
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">{noDataText}</div>
          ) : (
            filteredOptions.map((item) => {
              const selected = item[valueKey] === value;

              return (
                <button
                  key={item[valueKey]}
                  type="button"
                  onClick={() => {
                    onChange(item);
                    setSearch("");
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left hover:bg-blue-50 ${
                    selected ? "bg-blue-50" : ""
                  }`}
                >
                  <div>
                    <div className="font-semibold">{item[labelKey]}</div>

                    {subLabelKey && (
                      <div className="text-xs text-gray-500">
                        {item[subLabelKey]}
                      </div>
                    )}
                  </div>

                  {selected && <Check size={15} className="text-blue-600" />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
